import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Upload, X, Plus, FileText, Image, Video, Download, MessageSquare, Activity, Send, Save, Link as LinkIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api` || 'http://localhost:8001/api';

// Helper function to get current user information
const getCurrentUser = () => {
  try {
    const userData = localStorage.getItem('bizmanager_user');
    if (userData) {
      const user = JSON.parse(userData);
      
      if (user.isAdmin) {
        return {
          type: 'Admin',
          name: user.name || user.email || 'Admin User',
          id: user.id
        };
      } else if (user.isManager) {
        return {
          type: 'Manager',
          name: user.name || 'Manager User',
          id: user.id
        };
      } else if (user.isEmployee) {
        return {
          type: 'Employee',
          name: user.name || 'Employee User',
          id: user.id
        };
      } else if (user.isClient) {
        // For clients, use contact name from contacts array or contact_person
        const clientName = user.contacts?.[0]?.name || user.contact_person || user.name || 'Client User';
        return {
          type: 'Client',
          name: clientName,
          id: user.id
        };
      } else {
        return {
          type: 'User',
          name: user.name || user.email || 'User',
          id: user.id
        };
      }
    }
  } catch (e) {
    console.error('Error parsing user data:', e);
  }
  
  // Default fallback
  return {
    type: 'User',
    name: 'Current User',
    id: 'unknown'
  };
};

const ProjectTaskViewEnhanced = () => {
  const navigate = useNavigate();
  const { projectId, taskIndex } = useParams();
  const [project, setProject] = useState(null);
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  // Get user role information
  const [user, setUser] = useState(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    // Get user data from localStorage
    const userData = localStorage.getItem('bizmanager_user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      setIsClient(parsedUser.isClient || false);
    }
  }, []);

  const [taskDetails, setTaskDetails] = useState({
    task_name: '',
    task_description: '',
    task_documents: [], // Keep for display consistency
    task_links: [],
    task_notes: [],
    task_status: 'To Do'
  });

  const [newNote, setNewNote] = useState('');
  const [newLink, setNewLink] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  
  // Logs functionality
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    fetchProjectAndTask();
    fetchNotes();
    fetchLogs();
  }, [projectId, taskIndex]);

  // Helper function to check if current user is assigned to this project
  const isUserAssignedToProject = (projectData) => {
    if (!user || !projectData) return false;
    
    // Check if user is the project manager
    if (projectData.manager_id === user.id) return true;
    
    // Check if user is in the assigned employees list (primary access control)
    if (projectData.employee_ids && projectData.employee_ids.includes(user.id)) return true;
    
    return false;
  };

  const fetchProjectAndTask = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects/${projectId}`);
      const projectData = response.data;
      setProject(projectData);
      
      // Access control: Only allow admin, or employees/managers assigned to this project
      if (user && user.isEmployee && !isUserAssignedToProject(projectData)) {
        // If employee/manager is not assigned to this project, redirect or show access denied
        alert('Access denied. You are not assigned to this project.');
        navigate('/employee-dashboard');
        return;
      }

      if (projectData.tasks && projectData.tasks[taskIndex]) {
        const taskName = projectData.tasks[taskIndex];
        
        // Get ALL assignments for this task (there might be multiple employees assigned)
        const taskAssignments = projectData.task_assignments?.filter(
          assignment => assignment.task_index === parseInt(taskIndex)
        ) || [];

        setTask({
          name: taskName,
          index: parseInt(taskIndex),
          assignment: taskAssignments[0], // Keep first assignment for backward compatibility
          assignments: taskAssignments    // Store all assignments
        });

        // Consolidate documents, links, and notes from all task assignments
        const allDocuments = [];
        const allLinks = [];
        const allNotes = [];
        
        taskAssignments.forEach(assignment => {
          if (assignment.documents && Array.isArray(assignment.documents)) {
            allDocuments.push(...assignment.documents);
          }
          if (assignment.links && Array.isArray(assignment.links)) {
            allLinks.push(...assignment.links);
          }
          if (assignment.notes && Array.isArray(assignment.notes)) {
            allNotes.push(...assignment.notes);
          }
        });
        
        // Remove duplicates based on URL for documents and links
        const uniqueDocuments = allDocuments.filter((doc, index, arr) => 
          arr.findIndex(d => d.url === doc.url) === index
        );
        const uniqueLinks = allLinks.filter((link, index, arr) => 
          arr.findIndex(l => l.url === link.url) === index
        );
        
        setTaskDetails({
          task_name: taskName,
          task_description: taskAssignments[0]?.description || '',
          task_documents: uniqueDocuments,
          task_links: uniqueLinks,
          task_notes: allNotes,
          task_status: taskAssignments[0]?.status || 'To Do'
        });
      }
    } catch (error) {
      console.error('Error fetching project and task:', error);
      alert('Failed to load project task details');
      navigate('/support/tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/tasks/${taskIndex}/notes`);
      // Sort notes with latest first (same as project view)
      const sortedNotes = (response.data || []).sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setTaskDetails(prev => ({ ...prev, task_notes: sortedNotes }));
    } catch (error) {
      console.error('Error fetching notes:', error);
      // Use fallback notes from task assignment
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/tasks/${taskIndex}/logs`);
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
      setLogs([]);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadedFiles = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        uploadedFiles.push({
          name: file.name,
          url: response.data.url,
          type: file.type,
          size: file.size
        });
      }
      
      // Prepare data for backend API (use 'documents' field name for backend compatibility)
      const backendTaskDetails = {
        ...taskDetails,
        documents: [...taskDetails.task_documents, ...uploadedFiles]
      };
      delete backendTaskDetails.task_documents; // Remove frontend field name
      
      // Update frontend state
      const updatedTaskDetails = {
        ...taskDetails,
        task_documents: [...taskDetails.task_documents, ...uploadedFiles]
      };
      
      setTaskDetails(updatedTaskDetails);
      
      // Save the updated task details to backend
      try {
        await axios.put(`${API}/projects/${projectId}/tasks/${taskIndex}`, backendTaskDetails);
      } catch (saveError) {
        console.error('Error saving task details:', saveError);
      }
      
      // Log activity for each uploaded document
      for (const file of uploadedFiles) {
        await logActivity(`Document added: ${file.name}`);
      }
      
      // Refresh project data to ensure documents are loaded correctly after upload
      await fetchProjectAndTask();
      
      // Refresh logs to show the new activity
      fetchLogs();
      
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload some files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const removeDocument = (index) => {
    setTaskDetails(prev => ({
      ...prev,
      task_documents: prev.task_documents.filter((_, i) => i !== index)
    }));
  };

  const addLink = () => {
    if (newLink.trim()) {
      setTaskDetails(prev => ({
        ...prev,
        task_links: [...prev.task_links, newLink.trim()]
      }));
      setNewLink('');
    }
  };

  const removeLink = (index) => {
    setTaskDetails(prev => ({
      ...prev,
      task_links: prev.task_links.filter((_, i) => i !== index)
    }));
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    
    setAddingNote(true);
    try {
      // Get user data from localStorage
      const userData = localStorage.getItem('bizmanager_user');
      const user = userData ? JSON.parse(userData) : null;
      
      // Get proper user name based on role
      let userName = 'Unknown User';
      if (user) {
        if (user.isClient) {
          // For clients, use contact name
          userName = user.contacts?.[0]?.name || user.contact_person || user.name || 'Client User';
        } else if (user.isEmployee) {
          userName = user.name || 'Employee User';
        } else {
          userName = user.name || 'Admin User';
        }
      }
      
      const noteData = {
        content: newNote.trim(),
        created_by: user?.id,
        created_by_name: userName
      };
      
      await axios.post(`${API}/projects/${projectId}/tasks/${taskIndex}/notes`, noteData);
      
      // Add to local state for immediate update (latest first)
      const noteForState = {
        id: Date.now().toString(),
        content: newNote.trim(),
        created_at: new Date().toISOString(),
        created_by: user?.id,
        created_by_name: userName
      };
      
      setTaskDetails(prev => ({
        ...prev,
        task_notes: [noteForState, ...(prev.task_notes || [])]  // Latest first
      }));
      setNewNote('');
      
      await logActivity(`Added note: ${newNote.substring(0, 50)}${newNote.length > 50 ? '...' : ''}`);
      fetchLogs();
      
    } catch (error) {
      console.error('Error adding note:', error);
      // Still add to local state as fallback (latest first)
      const userData = localStorage.getItem('bizmanager_user');
      const user = userData ? JSON.parse(userData) : null;
      
      // Get proper user name for fallback
      let userName = 'Unknown User';
      if (user) {
        if (user.isClient) {
          userName = user.contacts?.[0]?.name || user.contact_person || user.name || 'Client User';
        } else if (user.isEmployee) {
          userName = user.name || 'Employee User';
        } else {
          userName = user.name || 'Admin User';
        }
      }
      
      const noteData = {
        id: Date.now().toString(),
        content: newNote.trim(),
        created_at: new Date().toISOString(),
        created_by: user?.id,
        created_by_name: userName
      };
      setTaskDetails(prev => ({
        ...prev,
        task_notes: [noteData, ...(prev.task_notes || [])]  // Latest first
      }));
      setNewNote('');
    } finally {
      setAddingNote(false);
    }
  };

  const updateTaskStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/projects/${projectId}/task-assignments/${taskIndex}/status`, {
        status: newStatus
      });
      
      setTaskDetails(prev => ({
        ...prev,
        task_status: newStatus
      }));
      
      await logActivity(`Status changed to ${newStatus}`);
      fetchLogs();
      
      alert('Task status updated successfully');
    } catch (error) {
      console.error('Error updating task status:', error);
      alert('Failed to update task status');
    } finally {
      setUpdating(false);
    }
  };

  const logActivity = async (activity) => {
    try {
      // Get user data from localStorage
      const userData = localStorage.getItem('bizmanager_user');
      const user = userData ? JSON.parse(userData) : null;
      
      // Get proper user name based on role
      let userName = 'Unknown User';
      if (user) {
        if (user.isClient) {
          // For clients, use contact name
          userName = user.contacts?.[0]?.name || user.contact_person || user.name || 'Client User';
        } else if (user.isEmployee) {
          userName = user.name || 'Employee User';
        } else {
          userName = user.name || 'Admin User';
        }
      }
      
      const logData = {
        activity,
        created_by: user?.id,
        created_by_name: userName,
        created_at: new Date().toISOString()
      };
      
      await axios.post(`${API}/projects/${projectId}/tasks/${taskIndex}/logs`, logData);
    } catch (error) {
      console.error('Error logging activity:', error);
      // Fallback to local state
      const userData = localStorage.getItem('bizmanager_user');
      const user = userData ? JSON.parse(userData) : null;
      
      // Get proper user name for fallback
      let userName = 'Unknown User';
      if (user) {
        if (user.isClient) {
          userName = user.contacts?.[0]?.name || user.contact_person || user.name || 'Client User';
        } else if (user.isEmployee) {
          userName = user.name || 'Employee User';
        } else {
          userName = user.name || 'Admin User';
        }
      }
      
      const logData = {
        id: Date.now().toString(),
        activity,
        created_by: user?.id,
        created_by_name: userName,
        created_at: new Date().toISOString()
      };
      setLogs(prev => [logData, ...prev]);
    }
  };

  const saveTaskDetails = async () => {
    setUpdating(true);
    try {
      await axios.put(`${API}/projects/${projectId}/tasks/${taskIndex}`, taskDetails);
      alert('Task details saved successfully');
    } catch (error) {
      console.error('Error saving task details:', error);
      alert('Task details updated locally');
    } finally {
      setUpdating(false);
    }
  };

  const downloadFile = async (fileUrl, fileName, index) => {
    try {
      // Use the backend download endpoint instead of direct file access
      const downloadUrl = `${API}/projects/${projectId}/tasks/${taskIndex}/documents/${index}/download`;
      
      // Create download link
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Log activity for document download
      await logActivity(`Document downloaded: ${fileName || 'file'}`);
      
      // Refresh logs to show the new activity
      fetchLogs();
      
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    }
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return <Image className="h-4 w-4" />;
    if (type?.startsWith('video/')) return <Video className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'To Do':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'In Testing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Resolved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Published (Prod)':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Closed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-500 ml-3">Loading task details...</p>
        </div>
      </div>
    );
  }

  if (!project || !task) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Task not found</h2>
          <p className="text-gray-500 mt-2">The requested project task could not be found.</p>
          <button
            onClick={() => navigate('/support/tasks')}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Tasks
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          {/* Back button removed to prevent incorrect navigation to Admin All Tasks */}
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{task.name}</h1>
            <p className="text-gray-600 mt-1">
              Task #T{task.index + 1} • Project: {project.name}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusBadgeClass(taskDetails.task_status)}`}>
            {taskDetails.task_status}
          </span>
          
          {/* Save button - Hidden for clients */}
          {!isClient && (
            <button
              onClick={saveTaskDetails}
              disabled={updating}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>Save Changes</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'details', name: 'Details', icon: FileText },
            { id: 'notes', name: 'Notes', icon: MessageSquare },
            { id: 'logs', name: 'Activity Logs', icon: Activity }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.name}</span>
              {tab.id === 'notes' && taskDetails.task_notes.length > 0 && (
                <span className="bg-blue-100 text-blue-600 text-xs rounded-full px-2 py-1">
                  {taskDetails.task_notes.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Task Description */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Task Description</h2>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-900 whitespace-pre-wrap">
                    {taskDetails.task_description || 'No description provided'}
                  </p>
                </div>
              </div>

              {/* Task Documents */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Task Documents</h2>
                
                {/* Upload Area - Hidden for clients */}
                {!isClient && (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center mb-4">
                    <input
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      id="document-upload"
                      accept="image/*,video/*,.pdf,.doc,.docx,.txt"
                    />
                    <label
                      htmlFor="document-upload"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">
                        Click to upload task documents
                      </span>
                      <span className="text-xs text-gray-400 mt-1">
                        Supports: Images, Videos, PDF, DOC, TXT
                      </span>
                    </label>
                  </div>
                )}

                {/* Documents List */}
                {taskDetails.task_documents.length > 0 && (
                  <div className="space-y-2">
                    {taskDetails.task_documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          {getFileIcon(doc.type)}
                          <div>
                            <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(doc.size)}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => downloadFile(doc.url, doc.name, index)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {uploading && (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-sm text-gray-500 mt-1">Uploading files...</p>
                  </div>
                )}
              </div>

              {/* Task Links */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Task Links</h2>
                
                {/* Add Link - Hidden for clients */}
                {!isClient && (
                  <div className="flex space-x-2 mb-4">
                    <input
                      type="url"
                      value={newLink}
                      onChange={(e) => setNewLink(e.target.value)}
                      placeholder="https://example.com/resource"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      onClick={addLink}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Add Link
                    </button>
                  </div>
                )}

                {/* Links List */}
                {taskDetails.task_links.length > 0 && (
                  <div className="space-y-2">
                    {taskDetails.task_links.map((link, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <LinkIcon className="h-4 w-4 text-gray-500" />
                          <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 truncate"
                          >
                            {link}
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Task Notes</h2>
                  <p className="text-gray-600 text-sm">Timeline-based task notes and updates</p>
                </div>
                <button
                  onClick={() => setNewNote('')}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  Add Note
                </button>
              </div>
              
              {/* Add Note Section */}
              <div className="mb-6 border-b border-gray-200 pb-6">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                  placeholder="Add a note about this task..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={addNote}
                    disabled={!newNote.trim() || addingNote}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <Send className="h-4 w-4" />
                    <span>{addingNote ? 'Adding...' : 'Add Note'}</span>
                  </button>
                </div>
              </div>

              {/* Timeline Notes */}
              {taskDetails.task_notes.length > 0 ? (
                <div className="space-y-4">
                  {taskDetails.task_notes.map((note, index) => (
                    <div key={note.id || index} className="relative">
                      {/* Timeline Line */}
                      {index < taskDetails.task_notes.length - 1 && (
                        <div className="absolute left-4 top-12 bottom-0 w-0.5 bg-gray-200"></div>
                      )}
                      
                      {/* Note Card */}
                      <div className="flex space-x-4">
                        {/* Timeline Dot */}
                        <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </div>
                        
                        {/* Note Content */}
                        <div className="flex-1 bg-white border rounded-lg p-4 shadow-sm">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="text-xs text-gray-500">
                                {format(new Date(note.created_at), "MMMM dd, yyyy 'at' h:mm a")}
                              </p>
                              <p className="text-xs text-blue-600 font-medium">
                                Added by: {note.created_by_name || 'Unknown User'}
                              </p>
                            </div>
                          </div>
                          <p className="text-gray-700 text-sm leading-relaxed">{note.content}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Notes Yet</h3>
                  <p className="text-gray-500 text-sm mb-4">Add task notes to track important updates and decisions</p>
                  <button
                    onClick={() => setNewNote('')}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    Add First Note
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'logs' && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Activity Logs</h2>
              
              <div className="space-y-4">
                {logs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Activity className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No activity logs yet.</p>
                  </div>
                ) : (
                  <div className="flow-root">
                    <ul className="-mb-8">
                      {logs.map((log, index) => (
                        <li key={log.id || index}>
                          <div className="relative pb-8">
                            {index !== logs.length - 1 && (
                              <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                            )}
                            <div className="relative flex space-x-3">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500 ring-8 ring-white">
                                <Activity className="h-4 w-4 text-white" />
                              </div>
                              <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                                <div>
                                  <p className="text-sm text-gray-900">{log.activity}</p>
                                  <p className="text-xs text-gray-500">{log.created_by_name || log.created_by || 'System'}</p>
                                </div>
                                <div className="whitespace-nowrap text-right text-sm text-gray-500">
                                  {new Date(log.created_at).toLocaleString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Task Info */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Task Information</h2>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Project</p>
                <p className="font-medium text-gray-900">{project.name}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Task ID</p>
                <p className="font-medium text-gray-900 font-mono">T{task.index + 1}</p>
              </div>

              {task.assignment && (
                <>
                  <div>
                    <p className="text-sm text-gray-500">Assigned Employee{task.assignments?.length > 1 ? 's' : ''}</p>
                    <div className="font-medium text-gray-900">
                      {(() => {
                        if (task.assignments && task.assignments.length > 0) {
                          // Filter out assignments with invalid employee names
                          const validAssignments = task.assignments.filter(assignment => {
                            const employeeName = assignment.employee_name || assignment.employeeName;
                            return employeeName && 
                                   employeeName.trim() !== '' && 
                                   employeeName.toLowerCase() !== 'unassigned';
                          });
                          
                          if (validAssignments.length > 0) {
                            if (validAssignments.length === 1) {
                              // Single employee
                              return validAssignments[0].employee_name || validAssignments[0].employeeName;
                            } else {
                              // Multiple employees - show as badges
                              return (
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {validAssignments.map((assignment, index) => {
                                    const employeeName = assignment.employee_name || assignment.employeeName;
                                    return (
                                      <div key={index} className="flex items-center space-x-1 bg-emerald-50 rounded-full px-2 py-1">
                                        <div className="w-4 h-4 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-medium">
                                          {employeeName.charAt(0).toUpperCase()}
                                        </div>
                                        <span className="text-xs text-emerald-700 font-medium">{employeeName}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            }
                          }
                        }
                        return 'No employee assigned';
                      })()}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Start Date</p>
                    <p className="font-medium text-gray-900">
                      {(() => {
                        try {
                          if (task.assignments && task.assignments.length > 0) {
                            // Find the earliest start date across all assignments
                            let earliestDate = null;
                            for (const assignment of task.assignments) {
                              const startDate = assignment.start_date || assignment.startDate;
                              if (startDate) {
                                const date = new Date(startDate + (startDate.includes('T') ? '' : 'T00:00:00'));
                                if (!earliestDate || date < earliestDate) {
                                  earliestDate = date;
                                }
                              }
                            }
                            return earliestDate ? earliestDate.toLocaleDateString() : 'Not set';
                          }
                          return 'Not set';
                        } catch (error) {
                          console.error('Start date formatting error:', error, task.assignments);
                          return 'Invalid date';
                        }
                      })()}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">End Date</p>
                    <p className="font-medium text-gray-900">
                      {(() => {
                        try {
                          if (task.assignments && task.assignments.length > 0) {
                            // Find the latest end date across all assignments
                            let latestDate = null;
                            for (const assignment of task.assignments) {
                              const endDate = assignment.end_date || assignment.endDate;
                              if (endDate) {
                                const date = new Date(endDate + (endDate.includes('T') ? '' : 'T00:00:00'));
                                if (!latestDate || date > latestDate) {
                                  latestDate = date;
                                }
                              }
                            }
                            return latestDate ? latestDate.toLocaleDateString() : 'Not set';
                          }
                          return 'Not set';
                        } catch (error) {
                          console.error('End date formatting error:', error, task.assignments);
                          return 'Invalid date';
                        }
                      })()}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Status Actions - Hidden for clients */}
          {!isClient && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Actions</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Update Status
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {['To Do', 'In Progress', 'In Testing', 'Resolved', 'Published (Prod)', 'Closed'].map(status => (
                      <button
                        key={status}
                        onClick={() => updateTaskStatus(status)}
                        disabled={updating || taskDetails.task_status === status}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          taskDetails.task_status === status
                            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                        }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectTaskViewEnhanced;